/**
 * 「按书录入」的**上传文件**入口 —— 纯逻辑 + 可注入的读取器（2026-10-20）。
 *
 * ## 用户的实际动作
 *
 * > "每次拿到一本语文书，会把后面的**识字表 / 写字表 / 词语表**拍照，让助手生成一个
 * >  **文本文件**，然后**上传到系统**录入。"
 *
 * 所以这个入口只做一件事：**把文件读成文本、填进粘贴框**。
 * ⛔ 它**不改**粘贴 / 预览 / 导入那条流程 —— 用户仍然能在框里看到内容并逐行核对，
 *    确认无误再点「预览」（这也是用户明确要求的："必须让用户能看到内容并核对"）。
 *
 * ## 文件格式（锁死，见 `@fht/shared` 的 `import-reading-tokens.ts`）
 *
 * 纯文本 UTF-8、一行 = 课本上的一行、每个词元 `字,拼音`、词元之间 Tab / 空格。
 * 所以这里**只负责解码与归一**，格式校验在解析器那一层（它才知道录的是哪张表）。
 *
 * ⚠️ 2026-10-20：拼音末尾的「不确定」标记 `?`（`得,dé?`，半角/全角都认）是
 *    **解析器**的事（`import-reading-tokens.ts` 先剥掉再归一，再交给预览标黄）。
 *    上传这一层**原样保留**它 —— ⛔ 不要在这里 trim / 删标点，否则"要用户确认"
 *    这个信息在进解析器之前就丢了。
 *
 * ## 三个必须挡住的坏文件（每条都是"人话"）
 *
 * | 情况 | 判据 | 给人的说法 |
 * | --- | --- | --- |
 * | 太大 | `file.size > 2 MB` | 说清多大、上限多少、怎么办（拆开/只保留部分行） |
 * | 不是 UTF-8 | `TextDecoder('utf-8', {fatal:true})` 抛错，或解出 U+FFFD | 「这个文件不是 UTF-8 编码，请另存为 UTF-8」 |
 * | 空的 | 归一后 `trim()` 为空 | 「这个文件是空的」 |
 *
 * ## 为什么用 `FileReader`（而不是 `file.arrayBuffer()`）
 *
 * 用户指定用**浏览器原生 FileReader**（不引依赖）。`FileReader` 从 IE10 起就有，
 * 比 `Blob.arrayBuffer()` 覆盖更广；这里只要二进制，编码判断自己做（见下）。
 *
 * ## 为什么解码逻辑与 `FileReader` 分开
 *
 * 本仓的 web 测试**不引 jsdom / @testing-library**（见 `apps/web/vitest.config.ts`），
 * 所以 `FileReader` 在测试里根本不存在。于是：
 *   · `decodeImportFileBytes()` 是纯函数（给字节、回文本 / 人话），直接可测；
 *   · `readImportFile()` 的读取器可以注入（`deps.load`），测试用假文件对象驱动。
 */

/**
 * 一个文件最多多大（**2 MB**）。
 *
 * 用户点名："文件**太大**（例如 > 2MB）→ 拒绝 + 一句人话"。
 * 真实的识字表/写字表/词语表文本只有几十 KB（410 个字 ≈ 5 KB），2 MB 已经是
 * 极端宽裕的上限；超过它多半是选错了文件（照片、PDF、整个学期的备份）。
 */
export const MAX_IMPORT_FILE_BYTES = 2 * 1024 * 1024;

/** 文件被拒的原因（`kind` 给测试与将来做埋点用；`message` 是给用户看的那句话）。 */
export type ImportFileRejectionKind = 'too-large' | 'not-utf8' | 'empty' | 'unreadable';

export interface ImportFileRejection {
  kind: ImportFileRejectionKind;
  /** 给用户看的一句话（人话，**必须**能照着做）。 */
  message: string;
}

/** 读成功的一份文件。 */
export interface ImportFileLoaded {
  /** 文件名（界面上显示「已载入：xxx.txt」）。 */
  name: string;
  /** 文件字节数（显示用）。 */
  bytes: number;
  /**
   * 解码 + 归一后的正文（BOM 已去掉、`\r\n` 已归一成 `\n`）。
   * ⚠️ 它**原样**填进粘贴框：不 trim、不删空行（行号要对得上原文）。
   */
  text: string;
  /** 非空行数（界面上「N 行」那个 N）。 */
  lineCount: number;
  /** 字符数（`text.length`，与粘贴框的计数器同口径）。 */
  charCount: number;
}

export type ImportFileResult =
  | { ok: true; file: ImportFileLoaded }
  | { ok: false; rejection: ImportFileRejection };

/** 非 UTF-8 的那句话（用户点名要这个意思；后面半句告诉他怎么修）。 */
export const NOT_UTF8_MESSAGE =
  '这个文件不是 UTF-8 编码，请另存为 UTF-8' +
  '（用记事本打开 →「另存为」→ 编码选「UTF-8」→ 再上传一次）。';

/** 把字节数写成人话（`3.4 MB` / `128 KB` / `21 B`）。 */
export function describeImportFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/**
 * 解码文件字节。
 *
 * ```text
 * 字节 → UTF-8 严格解码（fatal）→ 去 BOM → CRLF 归一成 LF → 查 U+FFFD
 * ```
 *
 * @returns `ok:false` 时 `message` 就是「不是 UTF-8」那句（给用户看）。
 */
export function decodeImportFileBytes(
  input: ArrayBuffer | Uint8Array,
): { ok: true; text: string } | { ok: false; message: string } {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);

  let decoded: string;
  try {
    /**
     * `fatal: true`：遇到非法字节序列**直接抛错**。
     * 这比"解出来一堆 U+FFFD 再猜"准得多 —— GBK 的 `.txt`（Windows 记事本默认
     * 另存为 ANSI）一定会在这一步失败。
     */
    decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    /** 环境不支持 `fatal`（极老浏览器）时退回宽松解码 + 下面的 U+FFFD 判断。 */
    try {
      decoded = new TextDecoder('utf-8').decode(bytes);
    } catch {
      return { ok: false, message: NOT_UTF8_MESSAGE };
    }
  }

  /**
   * ⚠️ 先剥 BOM 再判 U+FFFD：BOM 本身（U+FEFF）不是"乱码"，它是 Windows 记事本
   * "另存为 UTF-8"时**会加**的东西（用户点名要支持）。
   */
  const text = decoded.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');

  /** 宽松解码（或文件里本来就有替换字符）⇒ 依然是乱码，一律拒。 */
  if (text.includes('\uFFFD')) return { ok: false, message: NOT_UTF8_MESSAGE };

  return { ok: true, text };
}

/** 非空行数（用户看到的「N 行」）。 */
export function countNonEmptyLines(text: string): number {
  return text.split('\n').filter((line) => line.trim() !== '').length;
}

/**
 * 界面上那行「已载入」的文案（唯一实现；界面不许自己拼）。
 *
 * 用户点名要看到文件名，例如：
 *
 *     已载入：一年级下册识字表.txt（2 行 · 22 个字符，已填进下面的框，请核对）
 */
export function describeLoadedFile(file: ImportFileLoaded): string {
  return (
    `已载入：${file.name}` +
    `（${file.lineCount} 行 · ${file.charCount} 个字符，已填进下面的框，请核对）`
  );
}

/** 文件太大了那句话（带上实际大小与上限，用户才知道自己选错了什么）。 */
export function describeTooLarge(file: { name: string; size: number }): string {
  return (
    `这个文件太大了（${describeImportFileSize(file.size)}）——` +
    `「${file.name}」超过了一次 ${describeImportFileSize(MAX_IMPORT_FILE_BYTES)} 的上限。` +
    '请只保留这一张表（识字表 / 写字表 / 词语表）再上传，或者把文件拆成两次。'
  );
}

/** 用 `FileReader` 读二进制（原生，不引依赖）。 */
function readAsArrayBuffer(target: { name: string; size: number }): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    if (typeof FileReader === 'undefined') {
      reject(new Error('这个浏览器不支持读取本地文件'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const { result } = reader;
      if (result instanceof ArrayBuffer) {
        resolve(result);
        return;
      }
      reject(new Error('读到的不是文件内容'));
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('读取文件时出错了'));
    };
    reader.readAsArrayBuffer(target as unknown as Blob);
  });
}

/** `readImportFile()` 的依赖（只给测试注入假读取器用）。 */
export interface ReadImportFileDeps {
  /** 读整个文件的字节；默认 = `FileReader.readAsArrayBuffer()`。 */
  load?: (file: { name: string; size: number }) => Promise<ArrayBuffer>;
}

/**
 * 读一个用户选好的文件：**大小 → 编码 → 空文件** 三道闸，然后返回可以填框的文本。
 *
 * ⚠️ 它**不**解析格式、**不**判断"这一行是不是合法拼音"—— 那些是解析器的事
 *    （解析器才知道录的是哪张表）。这里失败时只负责给一句人话，粘贴框**不动**。
 */
export async function readImportFile(
  file: { name: string; size: number },
  deps: ReadImportFileDeps = {},
): Promise<ImportFileResult> {
  if (file.size > MAX_IMPORT_FILE_BYTES) {
    return { ok: false, rejection: { kind: 'too-large', message: describeTooLarge(file) } };
  }

  let buffer: ArrayBuffer;
  try {
    buffer = await (deps.load ?? readAsArrayBuffer)(file);
  } catch (error) {
    const reason = error instanceof Error && error.message !== '' ? error.message : '未知原因';
    return {
      ok: false,
      rejection: {
        kind: 'unreadable',
        message: `读不了「${file.name}」（${reason}）—— 请重试，或者把内容直接粘贴到下面的框里。`,
      },
    };
  }

  const decoded = decodeImportFileBytes(buffer);
  if (!decoded.ok) {
    return { ok: false, rejection: { kind: 'not-utf8', message: decoded.message } };
  }

  const { text } = decoded;
  if (text.trim() === '') {
    return {
      ok: false,
      rejection: {
        kind: 'empty',
        message: `「${file.name}」是空文件 —— 里面一个字都没有，请检查是不是选错了文件。`,
      },
    };
  }

  return {
    ok: true,
    file: {
      name: file.name,
      bytes: file.size,
      text,
      lineCount: countNonEmptyLines(text),
      charCount: text.length,
    },
  };
}
